import React from 'react';
import {
  Badge,
  Box,
  Divider,
  Group,
  Stack,
  Text,
  ThemeIcon,
  useComputedColorScheme,
} from '@mantine/core';
import {
  filterDisplayableTariffDifferences,
  formatField,
} from './TimelineUtils';
import { Tariff } from 'interfaces';
import { Diff, DiffEdit } from 'deep-diff';
import { formatDateTime } from 'utils/dateUtils';
import { useCurrentSiteStore } from 'store';
import { getStatusChipPresentation } from 'utils/badgeColors';
import {
  IconArrowRight,
  IconMinus,
  IconPencil,
  IconPlus,
} from '@tabler/icons-react';

interface TariffChangesProps {
  changeDetails: {
    added: Tariff[];
    removed: Tariff[];
    modified: {
      id: string;
      differences: Diff<any, any>[];
    }[];
    before: Tariff[];
    after: Tariff[];
    changedBy: string;
    updatedAt: string;
  };
  green: string;
  red: string;
}

export const TariffChanges: React.FC<TariffChangesProps> = ({
  changeDetails: { added, removed, modified, after },
}) => {
  const { timezone } = useCurrentSiteStore();
  const computedColorScheme = useComputedColorScheme('light');
  const dateTimeFields = new Set(['serviceStart', 'serviceEnd']);
  const getPathKey = (path: unknown) =>
    Array.isArray(path) ? path.join('.') : String(path ?? '');

  const formatDiffValue = (
    value: string | number | null | undefined,
    path: unknown
  ) => {
    if (value === null || value === undefined || value === '') return 'None';

    const field = getPathKey(path).split('.').at(-1);
    if (field && dateTimeFields.has(field)) {
      return formatDateTime(String(value), {
        timezone,
        fallback: String(value),
      });
    }

    return String(value);
  };

  const getTariffLabel = (id: string) => {
    const tariff = after.find((tariff) => tariff.id === id);
    if (!tariff) return 'Unknown';
    return `${tariff.prefix}${tariff.code}`;
  };

  const getTariffStatus = (tariff: Tariff | undefined) =>
    tariff?.status || 'Created';

  const displayableModified = modified
    .map((item) => ({
      ...item,
      differences: filterDisplayableTariffDifferences(item.differences),
    }))
    .filter((item) => item.differences.length > 0);

  const renderStatusTransition = (
    before: string,
    afterValue: string,
    ariaLabel?: string
  ) => {
    const beforeChip = getStatusChipPresentation(before, computedColorScheme);
    const afterChip = getStatusChipPresentation(
      afterValue,
      computedColorScheme
    );

    return (
      <Group
        gap="xs"
        wrap="nowrap"
        aria-label={ariaLabel ?? `Changed from ${before} to ${afterValue}`}
      >
        <Badge
          variant="light"
          radius="xl"
          style={{
            backgroundColor: beforeChip.backgroundColor,
            border: `1px solid ${beforeChip.borderColor}`,
            color: beforeChip.textColor,
          }}
        >
          {before}
        </Badge>
        <IconArrowRight size={16} color="gray" />
        <Badge
          variant="light"
          radius="xl"
          style={{
            backgroundColor: afterChip.backgroundColor,
            border: `1px solid ${afterChip.borderColor}`,
            color: afterChip.textColor,
          }}
        >
          {afterValue}
        </Badge>
      </Group>
    );
  };

  const renderTariffRow = ({
    label,
    before,
    afterValue,
    icon,
    iconColor,
    ariaLabel,
  }: {
    label: string;
    before: string;
    afterValue: string;
    icon: React.ReactNode;
    iconColor: string;
    ariaLabel?: string;
  }) => (
    <Group py={6} gap="sm" align="flex-start" data-testid="tariff-change-row">
      <ThemeIcon size={24} radius="sm" variant="light" color={iconColor}>
        {icon}
      </ThemeIcon>
      <Text fw={600} w={150} maw="100%" lh={1.4}>
        {label}
      </Text>
      {renderStatusTransition(before, afterValue, ariaLabel)}
    </Group>
  );

  return (
    <Box data-testid="tariff-changes-root">
      {/* Added */}
      {added && added.length > 0 && (
        <Box mb="xs" data-testid="tariff-changes-added">
          {added.map((t, idx) => (
            <Box key={`tariff-added-${idx}`} data-testid="tariff-added-item">
              {renderTariffRow({
                label: `Tariff ${t.prefix}${t.code}`,
                before: 'Created',
                afterValue: getTariffStatus(t),
                icon: <IconPlus size={14} />,
                iconColor: 'green',
              })}
            </Box>
          ))}
        </Box>
      )}

      {/* Removed */}
      {removed && removed.length > 0 && (
        <Box mb="xs" data-testid="tariff-changes-removed">
          {removed.map((t, idx) => (
            <Box
              key={`tariff-removed-${idx}`}
              data-testid="tariff-removed-item"
            >
              {renderTariffRow({
                label: `Tariff ${t.prefix}${t.code}`,
                before: getTariffStatus(t),
                afterValue: 'Removed',
                icon: <IconMinus size={14} />,
                iconColor: 'red',
              })}
            </Box>
          ))}
        </Box>
      )}

      {/* Modified */}
      {displayableModified.length > 0 && (
        <Box data-testid="tariff-changes-modified">
          <Stack gap={0}>
            {displayableModified.map(
              (
                {
                  id,
                  differences,
                }: { id: string; differences: DiffEdit<string, string>[] },
                idx: number
              ) => (
                <Box
                  key={`tariff-modified-${idx}`}
                  data-testid="tariff-modified-tariff"
                >
                  {idx > 0 && <Divider my="xs" />}
                  {differences?.map((diff, jdx) => {
                    const { path, lhs, rhs } = diff as DiffEdit<string>;
                    const pathKey = getPathKey(path);
                    const field = formatField(pathKey);
                    const before = formatDiffValue(lhs, path);
                    const afterValue = formatDiffValue(rhs, path);
                    const isStatusChange = pathKey === 'status';
                    return (
                      <Group
                        key={`tariff-modified-${idx}-${jdx}`}
                        py={6}
                        gap="sm"
                        align="flex-start"
                        data-testid="tariff-modified-diff"
                      >
                        <ThemeIcon
                          size={24}
                          radius="sm"
                          variant="light"
                          color="blue"
                        >
                          <IconPencil size={14} />
                        </ThemeIcon>
                        <Text fw={600} w={150} maw="100%" lh={1.4}>
                          Tariff {getTariffLabel(id)}
                        </Text>
                        {isStatusChange ? (
                          renderStatusTransition(
                            before,
                            afterValue,
                            `Status changed from ${before} to ${afterValue}`
                          )
                        ) : (
                          <Group gap="xs" style={{ flex: '1 1 260px' }}>
                            <Text size="sm" c="dimmed" w={105} maw="100%">
                              {field}:
                            </Text>
                            <Badge variant="light" color="gray" radius="xl">
                              {before}
                            </Badge>
                            <IconArrowRight size={16} color="gray" />
                            <Badge variant="light" color="blue" radius="xl">
                              {afterValue}
                            </Badge>
                          </Group>
                        )}
                      </Group>
                    );
                  })}
                </Box>
              )
            )}
          </Stack>
        </Box>
      )}
    </Box>
  );
};
